import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Shield, User as UserIcon } from 'lucide-react';
import { AuthContext } from '../../../contexts/AuthContext';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';

const initialsFrom = (value) => {
  const source = (value || '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const AdminTopBar = ({ onMobileOpen }) => {
  const { currentUser, userProfile, isSuperAdmin, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const displayName =
    userProfile?.displayName || userProfile?.username || currentUser?.email || 'Admin';
  const initials = initialsFrom(displayName || currentUser?.email);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border/60 bg-background/80 px-4 backdrop-blur sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        onClick={onMobileOpen}
        className="lg:hidden"
        aria-label="Open admin navigation"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="ml-auto flex items-center gap-2">
        {isSuperAdmin() && (
          <span className="hidden items-center gap-1.5 border border-heat/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-heat sm:inline-flex">
            <Shield className="h-3 w-3" />
            Super admin
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Account menu"
            >
              <Avatar className="h-9 w-9">
                {userProfile?.photoURL && <AvatarImage src={userProfile.photoURL} alt="" />}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="truncate text-sm">{displayName}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">
                {currentUser?.email}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {currentUser && (
              <DropdownMenuItem onSelect={() => navigate(`/user/${currentUser.uid}`)}>
                <UserIcon className="h-4 w-4" />
                Profile
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to="/">
                <UserIcon className="h-4 w-4" />
                View public site
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={async () => {
                await logout();
                navigate('/');
              }}
            >
              <LogOut className="h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default AdminTopBar;
